import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const resolveTarget = () => {
      // Honor a deep-link saved by ProtectedRoute before the OAuth round-trip,
      // or use the path the user was on before the hash arrived.
      try {
        const pending = sessionStorage.getItem('pendingRedirect');
        if (pending) {
          sessionStorage.removeItem('pendingRedirect');
          return pending;
        }
      } catch {}
      // If the OAuth callback redirected to a non-root path with the hash, keep it.
      if (window.location.pathname && window.location.pathname !== '/') {
        return window.location.pathname;
      }
      return '/dashboard';
    };

    const processAuth = async () => {
      const hash = location.hash;

      // Native Google OAuth: backend already created the session and put the
      // token in #auth_token=... We just persist it and refresh the auth state.
      const authTokenMatch = hash.match(/auth_token=([^&]+)/);
      if (authTokenMatch) {
        try {
          const token = decodeURIComponent(authTokenMatch[1]);
          localStorage.setItem('leaguehub-token', token);
          window.history.replaceState(null, '', window.location.pathname);
          if (typeof checkAuth === 'function') {
            await checkAuth();
          }
          navigate(resolveTarget(), { replace: true });
        } catch (error) {
          console.error('Auth callback (google) error:', error);
          navigate('/', { replace: true });
        }
        return;
      }

      // Legacy Emergent OAuth fallback (will be removed once native flow proven live).
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      if (!sessionIdMatch) {
        navigate('/', { replace: true });
        return;
      }
      try {
        const sessionId = sessionIdMatch[1];
        const userData = await login(sessionId);
        if (userData.token) {
          localStorage.setItem('leaguehub-token', userData.token);
        }
        window.history.replaceState(null, '', window.location.pathname);
        navigate(resolveTarget(), { replace: true, state: { user: userData.user } });
      } catch (error) {
        console.error('Auth callback (emergent) error:', error);
        navigate('/', { replace: true });
      }
    };

    processAuth();
  }, [location.hash, login, checkAuth, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Autenticazione in corso...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
